import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";

interface Props {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  label?: string;
}

/** Loads the next page when scrolled into view; the button covers keyboard and old browsers. */
export function LoadMoreSentinel({ hasMore, loading, onLoadMore, label = "Load more" }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!hasMore || loading || !node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { rootMargin: "240px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore]);

  if (!hasMore) return null;
  return (
    <div ref={ref} data-testid="load-more-sentinel" className="flex justify-center py-4">
      <Button type="button" variant="secondary" size="sm" loading={loading} onClick={onLoadMore}>
        {label}
      </Button>
    </div>
  );
}
